import type { ReactNode, Ref } from "react";
import { Icon } from "./Icon";
import "./disclosure.css";

type Props = {
  /** L'`id` della testata; il pannello prende `${id}-body`. */
  id: string;
  open: boolean;
  onToggle: () => void;
  /** Quello che la testata dice (nome e riassunto): è il nome del pulsante. */
  summary: ReactNode;
  /** Il contenuto del pannello. */
  children: ReactNode;
  /** La testata sta dentro un titolo di questo livello (un elenco di gruppi che si leggono come sezioni). */
  headingLevel?: 2 | 3 | 4 | 5 | 6;
  /** Il pannello chiuso resta nella pagina, nascosto (conserva quello che c'è dentro); di base si smonta. */
  keepMounted?: boolean;
  /** `compact`: righe da 36 px in un pannello di filtri. `regular`: righe da 44 px, testata in grassetto. */
  density?: "compact" | "regular";
  /** Arriva al pulsante della testata, per riportarvi il focus. */
  headRef?: Ref<HTMLButtonElement>;
  className?: string;
  bodyClassName?: string;
};

// Una testata che apre e chiude il suo pannello: `aria-expanded` sul pulsante e `aria-controls` verso il
// pannello quando c'è. La freccia cambia verso, il significato lo dice `aria-expanded`.
export function Disclosure({
  id,
  open,
  onToggle,
  summary,
  children,
  headingLevel,
  keepMounted = false,
  density = "regular",
  headRef,
  className,
  bodyClassName,
}: Props) {
  const bodyId = `${id}-body`;
  const mounted = open || keepMounted;
  const head = (
    <button
      ref={headRef}
      id={id}
      type="button"
      className="g-disclosure__head"
      aria-expanded={open}
      aria-controls={mounted ? bodyId : undefined}
      onClick={onToggle}
    >
      <span className="g-disclosure__summary">{summary}</span>
      <Icon name={open ? "up" : "down"} />
    </button>
  );
  const Heading = headingLevel ? (`h${headingLevel}` as const) : null;
  return (
    <div className={className ? `g-disclosure ${className}` : "g-disclosure"} data-density={density}>
      {Heading ? <Heading className="g-disclosure__heading">{head}</Heading> : head}
      {mounted ? (
        <div id={bodyId} className={bodyClassName ? `g-disclosure__body ${bodyClassName}` : "g-disclosure__body"} hidden={!open}>
          {children}
        </div>
      ) : null}
    </div>
  );
}
